import { useRouter } from "next/router";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../utils/firebase-config";
import moment from "moment";
import React, { useState, useEffect } from "react";
import {
  Box,
  Divider,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import EventAccordion from "@/components/Core/Events/EventAccordion";
import ImageComponent from "@/components/Elements/ImageComponent";
import SocialMediaLinks from "@/components/SocialMediaLinks";
import Script from "next/script";
import Head from "next/head";

// Function to convert Firestore GeoPoint to a plain object
function serializeGeoPoint(geoPoint) {
  return {
    latitude: geoPoint.latitude,
    longitude: geoPoint.longitude,
  };
}

function serializeCoordinates(coordinates) {
  if (
    coordinates &&
    typeof coordinates.latitude === "number" &&
    typeof coordinates.longitude === "number"
  ) {
    return {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    };
  }
  return null;
}

// Fetch band details
async function fetchBandByInternalLink(internalLink) {
  const bandsRef = collection(db, "bands");
  const bandQuery = query(
    bandsRef,
    where("internallink", "==", `/${internalLink.toLowerCase()}/`)
  );
  const querySnapshot = await getDocs(bandQuery);

  if (!querySnapshot.empty) {
    const bandDoc = querySnapshot.docs[0];
    return {
      id: bandDoc.id,
      ...bandDoc.data(),
    };
  } else {
    return null;
  }
}

// Fetch events with GeoPoint and coordinates serialization
async function fetchEventsByBandId(bandId, showPastEvents) {
  const eventsCollection = collection(db, "events");
  const fourHoursBefore = new Date();
  fourHoursBefore.setHours(fourHoursBefore.getHours() - 4);

  let eventQuery;
  if (showPastEvents) {
    eventQuery = query(
      eventsCollection,
      where("bandIds", "array-contains", bandId),
      orderBy("main_date", "asc")
    );
  } else {
    eventQuery = query(
      eventsCollection,
      where("bandIds", "array-contains", bandId),
      where("main_date", ">=", fourHoursBefore),
      orderBy("main_date", "asc")
    );
  }

  const querySnapshot = await getDocs(eventQuery);
  const events = querySnapshot.docs.map((doc) => {
    const eventData = doc.data();

    // Serialize GeoPoint if it exists
    if (eventData.g && eventData.g.geopoint) {
      eventData.g.geopoint = serializeGeoPoint(eventData.g.geopoint);
    }

    // Serialize coordinates if they exist
    if (eventData.coordinates) {
      eventData.coordinates = serializeCoordinates(eventData.coordinates);
    }

    return {
      id: doc.id,
      ...eventData,
      main_date: moment(eventData.main_date.toDate()).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
      modifieddate: moment(eventData.modifieddate?.toDate()).format(
        "YYYY-MM-DD HH:mm:ss"
      ),
    };
  });

  return events;
}

// Server-side generation of paths for each band page
export async function getStaticPaths() {
  // Fetch all the bands from Firestore
  const bandsQuery = query(collection(db, "bands"));
  const querySnapshot = await getDocs(bandsQuery);

  // Generate paths and filter out invalid or conflicting paths
  const paths = querySnapshot.docs
    .map((doc) => {
      const internalLink = doc.data().internallink.trim().replace(/\//g, "");

      // Ensure no empty links or links that match `/bands`
      if (!internalLink || internalLink === "bands") {
        return null; // Filter out this path
      }

      return { params: { internalLink } };
    })
    .filter(Boolean);

  return { paths, fallback: "blocking" };
}

// Fetch band data for static generation
export async function getStaticProps({ params }) {
  const internalLink = params.internalLink;
  const band = await fetchBandByInternalLink(internalLink);
  if (!band) {
    return { notFound: true };
  }

  const events = await fetchEventsByBandId(band.id, false);
  return {
    props: {
      band,
      events,
      internalLink,
    },
  };
}

// Band Details Component
const BandDetailsPage = ({ band, events: initialEvents, internalLink }) => {
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [events, setEvents] = useState(initialEvents);
  const [loading, setLoading] = useState(false);

  // Fetch past or upcoming events when toggle changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const fetchedEvents = await fetchEventsByBandId(band.id, showPastEvents);
      setEvents(fetchedEvents);
      setLoading(false);
    };

    fetchData();
  }, [showPastEvents]);

  const router = useRouter();
  const handleBack = () => router.back();

  const getPaginatedResults = () => {
    return events;
  };

  const transformedSocialMediaLinks = band?.socialMediaLinks
    .filter((link) => link.url && link.linkname !== "image") // Filter out objects with missing URLs
    .map((link) => {
      // Check if the url is relative, if so, make it absolute.
      if (link.url.startsWith("/")) {
        return `https://webtunes.com${link.url}`;
      }
      return link.url;
    });

  return (
    <>
      <Head>
        <title>{`${
          band?.bandname || "Band"
        } - Live Music Events | Webtunes`}</title>
        <meta
          name="description"
          content={`Discover live performances and events by ${band.bandname} on Webtunes.`}
        />
        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content={`${band?.bandname || "Band"} - Live Music Events | Webtunes`}
        />
        <meta
          property="og:description"
          content={`Explore live performances by ${
            band?.bandname || "the band"
          } on Webtunes.`}
        />
        <meta
          property="og:image"
          content={
            band?.imageUrl ||
            "https://webtunes.com/images/random/defaultband.jpg"
          }
        />
        <meta
          property="og:url"
          content={`https://webtunes.com/bands/${internalLink}`}
        />
        <meta property="og:type" content="website" />
        {/* Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${band?.bandname || "Band"} - Live Music Events | Webtunes`}
        />
        <meta
          name="twitter:description"
          content={`Discover live performances and events by ${
            band?.bandname || "the band"
          } on Webtunes.`}
        />
        <meta
          name="twitter:image"
          content={
            band?.imageUrl ||
            "https://webtunes.com/images/random/defaultband.jpg"
          }
        />
      </Head>

      <script
        id="json-ld-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MusicGroup",
            name: band?.bandname || "Band Name",
            image:
              band?.imageUrl ||
              "https://webtunes.com/images/random/defaultband.jpg",
            url: band?.website || `https://webtunes.com/bands/${internalLink}`,
            genre: band?.genre || undefined,
            description: band?.description || undefined,
            sameAs: transformedSocialMediaLinks || [], // transformedSocialMediaLinks
            album:
              band?.albums?.map((album) => ({
                // Map through albums if available
                "@type": "MusicAlbum",
                name: album.name,
                url: album.url,
                datePublished: album.releaseDate,
                image: album.imageUrl,
              })) || [],
            event: band?.events?.map((event) => ({
              // Events related to the band
              "@type": "Event",
              name: event.title,
              startDate: event.main_date,
              endDate: event.endDate || undefined,
              location: {
                "@type": "Place",
                name: event.venue_name || "Venue",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: event.address1,
                  addressLocality: event.venue_city,
                  addressRegion: event.venue_state,
                  postalCode: event.VENUE_ZIP,
                  addressCountry: "US",
                },
              },
              image:
                event.imageUrl ||
                "https://webtunes.com/images/random/defaultband.jpg",
              description: event.eventdescription || event.title,
              offers: {
                "@type": "Offer",
                url: event.ticket_url || "https://webtunes.com",
                price: "0",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
              },
            })),
          }),
        }}
      />

      {/* Google Analytics */}
      <Script
        async
        src="https://www.googletagmanager.com/gtag/js?id=G-10NX6JT8S2"
      />
      <Script
        id="google-analytics"
        dangerouslySetInnerHTML={{
          __html: `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-10NX6JT8S2');
    `,
        }}
      />

      <Box sx={{ padding: { xs: 0, sm: 3 }, margin: "0 auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Button onClick={handleBack} variant="contained">
            Back
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
              {band.bandname}
            </Typography>

            {Number(band.originals) === 1 && (
              <Typography
                variant="body1"
                sx={{ mt: 0, color: "text.secondary" }}
              >
                Has Originals
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: { md: "25%" }, width: "100%" }}>
            <ImageComponent imageUrl={band.imageUrl} altText={band.bandname} />
            {band.alias?.length ? (
              <Typography variant="h6" sx={{ mb: 3 }}>
                aka: <strong>{band.alias.join(", ")}</strong>
              </Typography>
            ) : null}
            <Box sx={{ display: "flex", mb: 3 }}>
              <SocialMediaLinks
                size={"48"}
                links={band.socialMediaLinks || []}
              />
            </Box>
            {band.description && (
              <div>
                {band.description
                  .trim()
                  .split("\n\n")
                  .map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
              </div>
            )}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 2, display: { xs: "none", md: "block" } }}
          />

          <Box sx={{ flex: 3, width: "100%" }}>
            <Typography
              variant="h5"
              sx={{
                textDecoration: "underline",
                mb: 2,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Events
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPastEvents}
                  onChange={() => setShowPastEvents(!showPastEvents)}
                />
              }
              label="Show Past Events"
            />
            {loading ? (
              <p>Loading events...</p>
            ) : (
              <EventAccordion
                data={getPaginatedResults()}
                showIndicators={true}
                totalCount={events.length}
                showCompactDesign={true}
                showImage={false}
                isMainView={false}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default BandDetailsPage;
